/** One entry of the ⌘K palette (P11, Ruling 10): navigation and commands, never the boards' content. */
export type PaletteCommand = {
  id: string;
  label: string;
  group: 'navigate' | 'board' | 'tools' | 'app';
  keywords?: string[];
  shortcut?: string;
  run: () => void;
};

/** Lower case without accents: « Modèles » is found by « modele ». */
export const fold = (s: string): string => s.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();

const words = (s: string): string[] => s.split(/[\s/·,.:()-]+/).filter(Boolean);

/** 0: the label starts with the word, 1: a word of the label does, 2: the label contains it, 3: a keyword matches. */
function wordScore(word: string, label: string, labelWords: string[], keywords: string[]): number | null {
  if (label.startsWith(word)) return 0;
  if (labelWords.some((w) => w.startsWith(word))) return 1;
  if (label.includes(word)) return 2;
  if (keywords.some((k) => k.includes(word))) return 3;
  return null;
}

/** Commands matching every word of the query, best first; ties and an empty query keep the given order. */
export function rankCommands(query: string, commands: readonly PaletteCommand[]): PaletteCommand[] {
  const q = words(fold(query));
  if (q.length === 0) return [...commands];
  const scored: { command: PaletteCommand; score: number }[] = [];
  for (const command of commands) {
    const label = fold(command.label);
    const labelWords = words(label);
    const keywords = (command.keywords ?? []).map(fold);
    let score = 0;
    for (const word of q) {
      const s = wordScore(word, label, labelWords, keywords);
      if (s === null) {
        score = -1;
        break;
      }
      score += s;
    }
    if (score >= 0) scored.push({ command, score });
  }
  return scored.sort((a, b) => a.score - b.score).map((s) => s.command);
}
